import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import PropTypes from "prop-types";
import "../AdmissionForm/AdmissionForm.css";

const API_URL = "https://aadyaacademy.org/api/";

function AdmissionForm({ handleCancel, fetchData, editingAdmissionList }) {
  const initialState = {
    student_name: "",
    student_aadhar: "",
    class_name: "",
    section: "",
    gender: "",
    dob: "",
    student_blood_group: "",
    academic_year: "",
    caste: "",
    nationality: "",
    mother_tongue: "",
    address: "",
    home_town: "",
    father_name: "",
    father_phone: "",
    father_email: "",
    father_occupation: "",
    father_aadhar: "",
    mother_name: "",
    mother_email: "",
    mother_phone: "",
    mother_occupation: "",
    mother_aadhar: "",
    guardian_name: "",
    guardian_phone: "",
    guardian_email: "",
    guardian_aadhar: "",
  };

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);

  // Set the initial form data if editing
  useEffect(() => {
    if (editingAdmissionList) {
      setFormData(editingAdmissionList);
    }
  }, [editingAdmissionList]);

  useEffect(() => {
    const fetchClassesSections = async () => {
      try {
        const [classResponse, sectionResponse] = await Promise.all([
          axios.get("https://aadyaacademy.org/api/classes"),
          axios.get("https://aadyaacademy.org/api/sections"),
        ]);
        setClasses(classResponse.data?.classes || []);
        setSections(sectionResponse.data?.sections || []);
      } catch (error) {
        console.error("Error fetching classes and sections: ", error);
      }
    };

    fetchClassesSections();
  }, []);

  const currentYear = new Date().getFullYear();
  const academicYears = Array.from(
    { length: 7 },
    (_, i) => `${currentYear - 5 + i}-${currentYear - 4 + i}`
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const studentApiUrl = "https://aadyaacademy.org/api/admission"; // API URL

      if (editingAdmissionList) {
        // Ensure student_id exists before making the request
        if (formData.student_id) {
          // Fetch the current data of the student to populate the form
          const response = await axios.get(`${studentApiUrl}/${formData.student_id}`);
          const studentData = response.data;

          // Optionally populate the form with the fetched student data here
          setFormData({
            ...formData,
            name: studentData.name,  // Example: Update the form fields
            age: studentData.age,    // Update with other student fields as required
            // Continue for all necessary fields
          });

          // Now make the PUT request without including student_id in the body
          await axios.put(`${studentApiUrl}/${formData.student_id}`, formData);
          toast.success("Student updated successfully!", { autoClose: 2000 });
        } else {
          toast.error("Student ID is missing for update!", { autoClose: 2000 });
          return; // Stop further execution
        }
      } else {
        await axios.post(studentApiUrl, formData);
        toast.success("Student added successfully!", { autoClose: 2000 });
      }

      fetchData(); // Refresh the data after a successful operation
      handleCancel(); // Reset the form
    } catch (error) {
      console.error("Error saving student: ", error);
      toast.error("Error saving student! Please try again.", { autoClose: 2000 });
    }
  };

  const handleDelete = async () => {
    if (formData.student_id) {
      const confirmation = window.confirm("Are you sure you want to delete this student permanently?");
      if (confirmation) {
        try {
          // Make the request to delete the student from the backend
          await axios.delete(`${API_URL}/${formData.student_id}`);
          toast.success("Student deleted successfully!", { autoClose: 2000 });

          // Update the state locally to remove the deleted student from the UI without needing a page refresh
          fetchData(); // Refresh the data after successful deletion

          handleCancel(); // Close the form
        } catch (error) {
          console.error("Error deleting student: ", error);
          toast.error("Error deleting student! Please try again.", { autoClose: 2000 });
        }
      } else {
        toast.info("Deletion canceled", { autoClose: 2000 });
      }
    } else {
      toast.error("No student selected for deletion.", { autoClose: 2000 });
    }
  };

  const handleClear = () => {
    setFormData(initialState);
    setErrors({});
  };

  // PropTypes for validation
  AdmissionForm.propTypes = {
    handleCancel: PropTypes.func.isRequired,
    fetchData: PropTypes.func.isRequired,
    editingAdmissionList: PropTypes.object,
  };
  

  return (
    <div className="admission">
      <div className="admission-c">
        <h2>{editingAdmissionList ? "Edit Admission" : "Admission"}</h2>
        <form className="admission-form" onSubmit={handleSubmit}>
          <div className="ad-f-n">
            <div className="stu-name">
              <h4>
                Student Name{" "}
                <FontAwesomeIcon icon={faStar} className="stu-icon" />
              </h4>
              <input
                className="studentname"
                type="text"
                name="student_name"
                value={formData.student_name}
                onChange={(e) => {
                  if (/^[a-zA-Z\s]*$/.test(e.target.value)) {
                    handleChange(e);
                  }
                }}
                placeholder="Enter"
                required
              />
            </div>
            <div className="stu-aadhar">
              <h4>
                Student Aadhar Number{" "}
                <FontAwesomeIcon icon={faStar} className="stu-icon" />
              </h4>
              <input
                className="studentAadhar"
                type="text"
                name="student_aadhar"
                value={formData.student_aadhar}
                onChange={(e) => {
                  if (
                    e.target.value.length <= 12 &&
                    /^[0-9]*$/.test(e.target.value)
                  ) {
                    handleChange(e);
                  }
                }}
                placeholder="Enter"
                pattern="\d{12}"
                title="Aadhar number should be exactly 12 digits"
              />
            </div>
            <div className="stu-class">
              <h4>
                Class <FontAwesomeIcon icon={faStar} className="stu-icon" />
              </h4>
              <select
                className="classname"
                name="class_name"
                value={formData.class_name}
                onChange={handleChange}
                required
              >
                <option value="class">Select Class</option>
                {classes.map((cls) => (
                  <option key={cls.id} value={cls.name}>
                    {cls.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="c-g-d">
            <div className="stu-section">
              <h4>
                Section <FontAwesomeIcon icon={faStar} className="stu-icon" />
              </h4>
              <select
                className="section"
                name="section"
                value={formData.section}
                onChange={handleChange}
              >
                <option value="">Select Section</option>
                {sections.map((section) => (
                  <option key={section.id} value={section.name}>
                    {section.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="stu-gender">
              <h4>
                Gender <FontAwesomeIcon icon={faStar} className="stu-icon" />
              </h4>
              <select
                className="gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                required
              >
                <option value="">Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
            <div className="stu-dob">
              <h4>
                Date of Birth{" "}
                <FontAwesomeIcon icon={faStar} className="stu-icon" />
              </h4>
              <input
                className="datepicker"
                type="date"
                name="dob"
                value={formData.dob ? new Date(formData.dob).toISOString().split("T")[0] : ""}
                onChange={handleChange}
                required
                max={new Date().toISOString().split("T")[0]}
              />
            </div>
            <div className="stu-blood">
              <h4>Blood Group</h4>
              <input
                className="studentBloodGroup"
                type="text"
                name="student_blood_group"
                value={formData.student_blood_group}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  const regex = /^(A|B|AB|O)?[+-]?$/;
                  if (regex.test(value)) {
                    handleChange(e);
                  }
                }}
                placeholder="Enter"
              />
            </div>
          </div>
          <div className="c-n-m">
            <div className="stu-academic">
              <h4>
                Academic Year{" "}
                <FontAwesomeIcon icon={faStar} className="stu-icon" />
              </h4>
              <select
                className="academic"
                name="academic_year"
                value={formData.academic_year}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  Select Academic Year
                </option>
                {academicYears.map((yearRange) => (
                  <option key={yearRange} value={yearRange}>
                    {yearRange}
                  </option>
                ))}
              </select>
            </div>
            <div className="stu-caste">
              <h4>Caste</h4>
              <select
                className="caste"
                name="caste"
                value={formData.caste}
                onChange={handleChange}
              >
                <option value="">Select Caste</option>
                <option value="General">General</option>
                <option value="SC">SC</option>
                <option value="ST">ST</option>
                <option value="OBC">OBC</option>
              </select>
            </div>
            <div className="stu-nationality">
              <h4>Nationality</h4>
              <input
                className="nationality"
                type="text"
                name="nationality"
                value={formData.nationality}
                onChange={(e) => {
                  if (/^[a-zA-Z\s]*$/.test(e.target.value)) {
                    handleChange(e);
                  }
                }}
                placeholder="Enter"
              />
            </div>
            <div className="stu-mother-tongue">
              <h4>Mother Tongue</h4>
              <input
                className="motherTongue"
                type="text"
                name="mother_tongue"
                value={formData.mother_tongue}
                onChange={(e) => {
                  if (/^[a-zA-Z\s]*$/.test(e.target.value)) {
                    handleChange(e);
                  }
                }}
                placeholder="Enter"
              />
            </div>
          </div>
          <div className="h-a">
            <div className="stu-address">
              <h4>Address</h4>
              <textarea
                className="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Enter"
              ></textarea>
            </div>
          </div>
          <div className="h-a">
            <div className="stu-hometown">
              <h4>Home Town</h4>
              <input
                className="homeTown"
                type="text"
                name="home_town"
                value={formData.home_town}
                onChange={handleChange}
                placeholder="Enter"
              />
            </div>
          </div>
          <div className="parent">
            <div className="parent-left">
              <div className="stu-father-name">
                <h4>
                  Father's Name{" "}
                  <FontAwesomeIcon icon={faStar} className="stu-icon" />
                </h4>
                <input
                  className="fathername"
                  type="text"
                  name="father_name"
                  value={formData.father_name}
                  onChange={(e) => {
                    if (/^[a-zA-Z\s]*$/.test(e.target.value)) {
                      handleChange(e);
                    }
                  }}
                  placeholder="Enter"
                  required
                />
              </div>

              <div className="stu-father-phone">
                <h4>
                  Father's Phone Number{" "}
                  <FontAwesomeIcon icon={faStar} className="stu-icon" />
                </h4>
                <input
                  className="fatherphone"
                  type="text"
                  name="father_phone"
                  value={formData.father_phone}
                  onChange={(e) => {
                    if (
                      e.target.value.length <= 10 &&
                      /^[0-9]*$/.test(e.target.value)
                    ) {
                      handleChange(e);
                    }
                  }}
                  placeholder="Enter"
                  required
                  pattern="\d{10}"
                  title="Phone number should be exactly 10 digits"
                />
              </div>
              <div className="stu-father-email">
                <h4>Father's Email</h4>
                <input
                  className="fatheremail"
                  type="email"
                  name="father_email"
                  value={formData.father_email}
                  onChange={handleChange}
                  placeholder="Enter"
                />
              </div>
              <div className="stu-father-occupation">
                <h4>Father's Occupation</h4>
                <input
                  className="fatherOccupation"
                  type="text"
                  name="father_occupation"
                  value={formData.father_occupation}
                  onChange={(e) => {
                    if (/^[a-zA-Z\s]*$/.test(e.target.value)) {
                      handleChange(e);
                    }
                  }}
                  placeholder="Enter"
                />
              </div>
              <div className="stu-father-aadhar">
                <h4>Father's Aadhar Number</h4>
                <input
                  className="fatherAadhar"
                  type="text"
                  name="father_aadhar"
                  value={formData.father_aadhar}
                  onChange={(e) => {
                    if (
                      e.target.value.length <= 12 &&
                      /^[0-9]*$/.test(e.target.value)
                    ) {
                      handleChange(e);
                    }
                  }}
                  placeholder="Enter"
                  pattern="\d{12}"
                  title="Aadhar number should be exactly 12 digits"
                />
              </div>
            </div>
            <div className="parent-mid">
              <div className="stu-mother-name">
                <h4>Mother's Name</h4>
                <input
                  className="motherName"
                  type="text"
                  name="mother_name"
                  value={formData.mother_name}
                  onChange={(e) => {
                    if (/^[a-zA-Z\s]*$/.test(e.target.value)) {
                      handleChange(e);
                    }
                  }}
                  placeholder="Enter"
                />
              </div>
              <div className="stu-mother-phone">
                <h4>Mother's Phone Number</h4>
                <input
                  className="motherPhone"
                  type="text"
                  name="mother_phone"
                  value={formData.mother_phone}
                  onChange={(e) => {
                    if (
                      e.target.value.length <= 10 &&
                      /^[0-9]*$/.test(e.target.value)
                    ) {
                      handleChange(e);
                    }
                  }}
                  placeholder="Enter"
                  pattern="\d{10}"
                  title="Phone number should be exactly 10 digits"
                />
              </div>
              <div className="stu-mother-email">
                <h4>Mother's Email</h4>
                <input
                  className="motherEmail"
                  type="email"
                  name="mother_email"
                  value={formData.mother_email}
                  onChange={handleChange}
                  placeholder="Enter"
                />
              </div>
              <div className="stu-mother-occupation">
                <h4>Mother's Occupation</h4>
                <input
                  className="motherOccupation"
                  type="text"
                  name="mother_occupation"
                  value={formData.mother_occupation}
                  onChange={(e) => {
                    if (/^[a-zA-Z\s]*$/.test(e.target.value)) {
                      handleChange(e);
                    }
                  }}
                  placeholder="Enter"
                />
              </div>
              <div className="stu-mother-aadhar">
                <h4>Mother's Aadhar Number</h4>
                <input
                  className="motherAadhar"
                  type="text"
                  name="mother_aadhar"
                  value={formData.mother_aadhar}
                  onChange={(e) => {
                    if (
                      e.target.value.length <= 12 &&
                      /^[0-9]*$/.test(e.target.value)
                    ) {
                      handleChange(e);
                    }
                  }}
                  placeholder="Enter"
                  pattern="\d{12}"
                  title="Aadhar number should be exactly 12 digits"
                />
              </div>
            </div>
            <div className="parent-right">
              <div className="stu-guardian-name">
                <h4>Guardian's Name</h4>
                <input
                  className="guardianName"
                  type="text"
                  name="guardian_name"
                  value={formData.guardian_name}
                  onChange={(e) => {
                    if (/^[a-zA-Z\s]*$/.test(e.target.value)) {
                      handleChange(e);
                    }
                  }}
                  placeholder="Enter"
                />
              </div>
              <div className="stu-guardian-phone">
                <h4>Guardian's Phone Number</h4>
                <input
                  className="guardianPhone"
                  type="text"
                  name="guardian_phone"
                  value={formData.guardian_phone}
                  onChange={(e) => {
                    if (
                      e.target.value.length <= 10 &&
                      /^[0-9]*$/.test(e.target.value)
                    ) {
                      handleChange(e);
                    }
                  }}
                  placeholder="Enter"
                  pattern="\d{10}"
                  title="Phone number should be exactly 10 digits"
                />
              </div>
              <div className="stu-guardian-email">
                <h4>Guardian's Email</h4>
                <input
                  className="guardianEmail"
                  type="email"
                  name="guardian_email"
                  value={formData.guardian_email}
                  onChange={handleChange}
                  placeholder="Enter"
                />
              </div>
              <div className="stu-guardian-aadhar">
                <h4>Guardian 's Aadhar Number</h4>
                <input
                  className="guardianAadhar"
                  type="text"
                  name="guardian_aadhar"
                  value={formData.guardian_aadhar}
                  onChange={(e) => {
                    if (
                      e.target.value.length <= 12 &&
                      /^[0-9]*$/.test(e.target.value)
                    ) {
                      handleChange(e);
                    }
                  }}
                  placeholder="Enter"
                  pattern="\d{12}"
                  title="Aadhar number should be exactly 12 digits"
                />
              </div>
            </div>
          </div>

         <div className="form-buttons">
            <button className="submit" type="submit">
              Submit
            </button>
            <button className="clear" type="button" onClick={handleClear}>
              Clear
            </button>
            <button className="cancel" type="button" onClick={handleCancel}>
              Cancel
            </button>

            {editingAdmissionList && (
              <button
                className="delete"
                type="button"
                onClick={handleDelete}
              >
                Delete
              </button>
            )}
          </div>
        </form>
      </div>
      <ToastContainer />
    </div>
  );
}

export default AdmissionForm;